import { CModal, CSpinner } from "@coreui/react";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import React, { useEffect, useMemo, useReducer, useState } from "react";
import DeleteIcon from "../../components/icons/DeleteIcon";
import Pagination from "../../components/ui/Pagination";
import RenderTable from "../../components/ui/RenderTable";
import { DELETE_ACTIONS } from "../../config/constants";
import { colors } from "../../config/theme";
import {
  bulkDeleteReducer,
  bulkDeleteState,
} from "../../context/bulkDeleteReducer";
import { useCategoryQuery } from "../../hooks/category/useCategoryQuery";
import { usePaginatedContent } from "../../hooks/usePaginatedContent";
import { general } from "../../locales/general";
import AdminRoute from "../auth/RestrictedRoute";
import CategoriesTable from "./CategoryTable";
import NewCategory from "./NewCategory";
import { Chip } from "../../helpers/general";
import { cardStyle } from "../Places/PlacesTable";
import { controlStyle } from "../../helpers/constants";

const PAGE_SIZE = 10;

export default function Categories() {
  const [currentPage, setCurrentPage] = useState(1);
  const [addVisible, setAddVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const { categories, fetchingCategories, deleteCategory, mutating } =
    useCategoryQuery();

  // Search goes back to page 1
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const filteredCategories = useMemo(
    () =>
      (categories ?? []).filter((category: any) =>
        category.label?.toLowerCase()?.includes(searchQuery),
      ),
    [categories, searchQuery],
  );

  // Derived value, no state or effect needed
  const totalPages = Math.ceil(filteredCategories.length / PAGE_SIZE);

  const paginatedContent = usePaginatedContent(
    currentPage,
    PAGE_SIZE,
    filteredCategories,
  );

  const [selected, dispatch] = useReducer(bulkDeleteReducer, bulkDeleteState);
  const selectedCount = selected?.ids?.length ?? 0;

  const handleBulkDelete = () => {
    [...selected.ids].forEach((id: string) => {
      if (!id) return;
      deleteCategory(id);
      dispatch({ type: DELETE_ACTIONS.REMOVE, id });
    });
  };

  const total = categories?.length ?? 0;

  return (
    <AdminRoute>
      <section>
        {/* Title + count + add */}
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
          <div className="d-flex align-items-center gap-2">
            <h5 className="fs-4 fw-bold mb-0">Catégories</h5>
            {categories && (
              <Chip tone="warning">
                {total} {total > 1 ? "Catégories" : "Catégorie"}
              </Chip>
            )}
          </div>

          <button
            type="button"
            onClick={() => setAddVisible(true)}
            className="btn btn-primary text-white rounded-pill px-4 border-0"
          >
            + Ajouter une catégorie
          </button>
        </div>

        {/* Toolbar */}
        <div
          style={{ ...cardStyle, padding: "12px 16px" }}
          className="d-flex flex-wrap align-items-center gap-2 mb-3"
        >
          <div
            className="d-flex align-items-center gap-2 px-3 flex-grow-1"
            style={{ ...controlStyle, minWidth: 200, maxWidth: 360 }}
          >
            <MagnifyingGlassIcon color="gray" width={18} />
            <input
              type="text"
              placeholder="Rechercher une catégorie"
              className="border-0 w-100 bg-transparent"
              style={{ outline: "none", fontSize: 13 }}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value.toLowerCase())}
            />
          </div>

          <button
            type="button"
            disabled={mutating || selectedCount === 0}
            onClick={handleBulkDelete}
            className="btn btn-outline-danger rounded-pill ms-auto d-flex align-items-center gap-2 px-3"
          >
            {mutating ? (
              <>
                <CSpinner size="sm" /> {general.fr.wait}
              </>
            ) : (
              <>
                <DeleteIcon fill={colors.primary} /> Supprimer
                {selectedCount > 0 && ` (${selectedCount})`}
              </>
            )}
          </button>
        </div>

        <CModal
          size="lg"
          alignment="center"
          visible={addVisible}
          onClose={() => setAddVisible(false)}
        >
          <NewCategory
            onAccept={() => {}}
            onClose={() => setAddVisible(false)}
          />
        </CModal>

        {/* Table */}
        <div style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
          <RenderTable
            loading={fetchingCategories}
            render={() =>
              paginatedContent.length > 0 ? (
                <CategoriesTable
                  categories={paginatedContent as Category[]}
                  categoriesToBeDeletedList={dispatch}
                />
              ) : (
                <p className="text-center text-muted py-5 mb-0">
                  {general.fr.noData}
                </p>
              )
            }
          />
        </div>

        <Pagination
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          siblingCount={1}
          totalCount={totalPages}
          pageSize={PAGE_SIZE}
        />
      </section>
    </AdminRoute>
  );
}
